import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { MagnifyingGlass, Crosshair, MapPin } from "@phosphor-icons/react";

// Default leaflet icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const CHILE_CENTER = [-33.45, -70.65];

function ClickHandler({ onPick }) {
  useMapEvents({
    click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }),
  });
  return null;
}

function FlyToLocation({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position && position.lat != null && position.lng != null) {
      map.flyTo([position.lat, position.lng], 13, { duration: 0.8 });
    }
    // eslint-disable-next-line
  }, [position?.lat, position?.lng]);
  return null;
}

/**
 * MapPicker — selección interactiva de coordenadas usando Leaflet + Nominatim.
 *
 * Props:
 *  - value: { latitude, longitude }
 *  - onChange: ({ latitude, longitude }) => void
 *  - autoQuery: string opcional — al cambiar, busca esa dirección automáticamente
 *  - height: alto del mapa (default 280)
 */
export default function MapPicker({ value = {}, onChange, autoQuery = "", height = 280 }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const lastAutoQuery = useRef("");

  const position = useMemo(() => {
    if (value?.latitude != null && value?.latitude !== "" && value?.longitude != null && value?.longitude !== "") {
      return { lat: Number(value.latitude), lng: Number(value.longitude) };
    }
    return null;
  }, [value?.latitude, value?.longitude]);

  // Auto-search when region/comuna changes from outside
  useEffect(() => {
    if (!autoQuery || autoQuery === lastAutoQuery.current) return;
    lastAutoQuery.current = autoQuery;
    runSearch(autoQuery, true);
    // eslint-disable-next-line
  }, [autoQuery]);

  const runSearch = async (q, silent = false) => {
    if (!q || q.trim().length < 2) return;
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=cl&limit=6&q=${encodeURIComponent(q)}`;
      const r = await fetch(url, { headers: { "Accept-Language": "es" } });
      const data = await r.json();
      setResults(data || []);
      if (silent && data && data.length > 0) {
        // Auto-pick first result only if no coordinates yet
        if (!position) {
          const f = data[0];
          onChange({ latitude: parseFloat(f.lat), longitude: parseFloat(f.lon) });
        }
      }
    } catch (e) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const pickResult = (r) => {
    onChange({ latitude: parseFloat(r.lat), longitude: parseFloat(r.lon) });
    setResults([]);
    setQuery(r.display_name);
  };

  const useGeolocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => {}
    );
  };

  const handleMapClick = (latlng) => {
    onChange({ latitude: latlng.lat, longitude: latlng.lng });
  };

  return (
    <div className="space-y-2" data-testid="map-picker">
      {/* Search bar */}
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar dirección, comuna, sector…"
            className="pl-8 h-9 text-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(query); } }}
            data-testid="map-picker-search"
          />
          {results.length > 0 && (
            <ul className="absolute z-[1000] left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-md">
              {results.map((r) => (
                <li key={r.place_id}>
                  <button
                    type="button"
                    onClick={() => pickResult(r)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b last:border-b-0 border-slate-100"
                  >
                    <div className="font-medium text-slate-900 truncate">{r.display_name.split(",").slice(0, 2).join(",")}</div>
                    <div className="text-[10px] text-slate-500 truncate">{r.display_name}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button type="button" size="sm" variant="secondary" onClick={() => runSearch(query)} disabled={searching}>
          {searching ? "…" : "Buscar"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={useGeolocation} title="Usar mi ubicación">
          <Crosshair size={14} />
        </Button>
      </div>

      {/* Map */}
      <div
        className="rounded-md border border-slate-200 overflow-hidden bg-white"
        style={{ height }}
        data-testid="map-picker-map"
      >
        <MapContainer
          center={position ? [position.lat, position.lng] : CHILE_CENTER}
          zoom={position ? 13 : 5}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={handleMapClick} />
          {position && <FlyToLocation position={position} />}
          {position && (
            <Marker
              position={[position.lat, position.lng]}
              draggable={true}
              eventHandlers={{
                dragend: (e) => {
                  const ll = e.target.getLatLng();
                  onChange({ latitude: ll.lat, longitude: ll.lng });
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      {/* Coordinates inline */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <MapPin size={12} />
          {position ? (
            <span className="font-mono text-slate-700">
              {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
            </span>
          ) : (
            <span>Click en el mapa o busca para fijar la ubicación.</span>
          )}
        </div>
        {position && (
          <button
            type="button"
            onClick={() => onChange({ latitude: "", longitude: "" })}
            className="text-xs text-red-600 hover:underline"
            data-testid="map-picker-clear"
          >
            Limpiar
          </button>
        )}
      </div>
    </div>
  );
}
